import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrial } from '../context/TrialContext';
import { formatPrice } from '../utils/astropayService';
import { AlertTriangle, CreditCard, Clock, ArrowRight, Loader2 } from 'lucide-react';

/**
 * ReadOnlyBanner — banner persistente en el ERP.
 *
 * Niveles (de menos a más urgente):
 *   info azul     → "pago en proceso" (esperando confirmación de MP)
 *   info azul     → trial por vencer (≤5 días)
 *   warning naran → período de gracia
 *   crítico ámbar → vencido / read-only / bloqueado
 *
 * Cobro 100% Mercado Pago / AstroPay. Sin comprobantes manuales.
 */
export default function ReadOnlyBanner() {
  const navigate = useNavigate();
  const {
    isReadOnly,
    isGrace,
    isExpired,
    isBlocked,
    isTrialing,
    isActive,
    isPending,
    showGraceWarning,
    showWarning,
    currentPlan,
    currentPlanKey,
    paymentUrl,
    graceDaysLeft,
    daysLeft,
    billingProviderLabel,
  } = useTrial();

  // No mostrar si todo está OK
  if (isActive && !showWarning && !isPending) return null;
  if (isTrialing && !showWarning && !showGraceWarning && !isPending) return null;
  if (!isReadOnly && !isGrace && !showWarning && !showGraceWarning && !isPending) return null;

  const planName = currentPlan?.name || currentPlanKey || 'tu plan';
  const price = currentPlan?.price_monthly || 0;

  // ── Pago en proceso (esperando confirmación de MP) ───────────
  // Tiene prioridad sobre warning de trial: si el usuario ya pagó,
  // no queremos asustarlo con "trial por vencer".
  if (isPending) {
    return (
      <div
        className="w-full px-4 py-2.5 flex flex-wrap items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
          borderBottom: '2px solid #60A5FA',
        }}
      >
        <div className="flex items-center gap-3">
          <Loader2 size={16} className="text-blue-500 animate-spin shrink-0" />
          <p className="text-[11px] font-bold text-blue-800">
            Estamos esperando la confirmación de {billingProviderLabel}.{' '}
            <span className="font-normal">Apenas se acredite el pago, tu cuenta se activa sola.</span>
          </p>
        </div>
        <button
          onClick={() => navigate('/sistema/gestion-plan')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/70 text-blue-700 text-[10px] font-black uppercase tracking-widest border border-blue-300 hover:bg-white transition-all shrink-0"
        >
          Ver estado
          <ArrowRight size={12} />
        </button>
      </div>
    );
  }

  // ── Expired / Read-only banner ───────────────────────────────
  if (isReadOnly || isExpired || isBlocked) {
    return (
      <div
        className="w-full px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
          borderBottom: '2px solid #F59E0B',
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
            <AlertTriangle size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[12px] font-black text-amber-900 uppercase tracking-wide">
              {isBlocked ? 'Cuenta bloqueada' : 'Suscripción vencida — Modo solo lectura'}
            </p>
            <p className="text-[11px] text-amber-800 font-medium">
              {isBlocked
                ? 'Contactá al soporte de Gastify para reactivar tu cuenta.'
                : `${planName}: ${formatPrice(price)}/mes. Podés ver toda tu información pero no podés operar.`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {!isBlocked && (
            <>
              {paymentUrl ? (
                <a
                  href={paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-[12px] font-black uppercase tracking-[0.08em] hover:bg-amber-700 transition-all shadow-sm"
                >
                  <CreditCard size={14} />
                  Pagar mi plan
                  <ArrowRight size={12} />
                </a>
              ) : (
                <button
                  onClick={() => navigate('/sistema/gestion-plan')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-[12px] font-black uppercase tracking-[0.08em] hover:bg-amber-700 transition-all shadow-sm"
                >
                  <CreditCard size={14} />
                  Pagar mi plan
                  <ArrowRight size={12} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Grace period warning banner ──────────────────────────────
  if (isGrace || showGraceWarning) {
    return (
      <div
        className="w-full px-4 py-2.5 flex flex-wrap items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
          borderBottom: '2px solid #FB923C',
        }}
      >
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-orange-500 shrink-0" />
          <p className="text-[11px] font-bold text-orange-800">
            Tu período de prueba terminó.{' '}
            {graceDaysLeft > 0
              ? `Tenés ${graceDaysLeft} día${graceDaysLeft === 1 ? '' : 's'} antes de que se active el modo solo lectura.`
              : 'El modo solo lectura se activará pronto.'}
            {' '}
            <span className="font-black">{planName}: {formatPrice(price)}/mes</span>
          </p>
        </div>
        <button
          onClick={() => navigate('/sistema/gestion-plan')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-[12px] font-black uppercase tracking-[0.08em] hover:bg-orange-600 transition-all shadow-sm shrink-0"
        >
          <CreditCard size={13} />
          Pagar mi plan
          <ArrowRight size={12} />
        </button>
      </div>
    );
  }

  // ── Trial ending warning (≤5 days) ───────────────────────────
  if (showWarning) {
    return (
      <div
        className="w-full px-4 py-2 flex flex-wrap items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
          borderBottom: '2px solid #60A5FA',
        }}
      >
        <div className="flex items-center gap-3">
          <Clock size={16} className="text-blue-500 shrink-0" />
          <p className="text-[11px] font-bold text-blue-800">
            Tu prueba gratuita termina en {daysLeft} día{daysLeft === 1 ? '' : 's'}.{' '}
            Pagá ahora y los días que te quedan se suman al primer mes.
          </p>
        </div>
        <button
          onClick={() => navigate('/sistema/gestion-plan')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-[12px] font-black uppercase tracking-[0.08em] hover:bg-blue-600 transition-all shadow-sm shrink-0"
        >
          <CreditCard size={13} />
          Pagar mi plan
          <ArrowRight size={12} />
        </button>
      </div>
    );
  }

  return null;
}
